// ============================================
// MODAL COMMON - Reusable Modal Store & Utilities
// ============================================
//
// This provides a global store for managing modals and utilities
// for body scroll locking.
//
// Usage with PHP component:
// 1. PHP component uses x-modal-common:register="modalId" to register
// 2. Open: $store.modalCommon.open('modalId') or showXxxModal = true
// 3. Close: $store.modalCommon.close('modalId') or showXxxModal = false
//
// The store automatically handles:
// - Body scroll locking when modal opens
// - Restoring scroll when all modals close
// - Escape key to close modals
// ============================================

import Alpine from 'alpinejs';

interface ModalContext {
    urgent?: boolean;
    urgentMessage?: string;
    [key: string]: unknown;
}

interface ModalState {
    isOpen: boolean;
    context: ModalContext | null;
    onOpen?: () => void;
    onClose?: () => void;
}

interface ModalCommonStore {
    modals: Record<string, ModalState>;
    context: ModalContext | null;
    register(id: string, options?: { onOpen?: () => void; onClose?: () => void }): void;
    unregister(id: string): void;
    open(id: string, context?: ModalContext): void;
    close(id: string): void;
    closeAll(): void;
    toggle(id: string): void;
    isOpen(id: string): boolean;
    hasOpenModals(): boolean;
}

export function registerModalCommon(): void {
    // Global store for managing all modals
    Alpine.store('modalCommon', {
        modals: {} as Record<string, ModalState>,
        context: null as ModalContext | null,

        register(id: string, options: { onOpen?: () => void; onClose?: () => void } = {}) {
            if (!this.modals[id]) {
                this.modals[id] = {
                    isOpen: false,
                    context: null,
                    onOpen: options.onOpen,
                    onClose: options.onClose
                };
            }
        },

        unregister(id: string) {
            if (this.modals[id]?.isOpen) {
                this.close(id);
            }
            delete this.modals[id];
        },

        open(id: string, context: ModalContext | null = null) {
            if (!this.modals[id]) {
                this.register(id);
            }

            this.modals[id].isOpen = true;
            this.modals[id].context = context;
            this.context = context;
            document.body.style.overflow = 'hidden';

            // Call onOpen callback if exists
            if (this.modals[id].onOpen) {
                this.modals[id].onOpen();
            }
        },

        close(id: string) {
            if (this.modals[id]) {
                this.modals[id].isOpen = false;
                this.modals[id].context = null;
                this.context = null;

                // Call onClose callback if exists
                if (this.modals[id].onClose) {
                    this.modals[id].onClose();
                }
            }

            // Only restore scroll if no other modals are open
            if (!this.hasOpenModals()) {
                document.body.style.overflow = '';
            }
        },

        closeAll() {
            Object.keys(this.modals).forEach(id => {
                if (this.modals[id].isOpen) {
                    this.modals[id].isOpen = false;
                    if (this.modals[id].onClose) {
                        this.modals[id].onClose();
                    }
                }
            });
            document.body.style.overflow = '';
        },

        toggle(id: string) {
            if (this.isOpen(id)) {
                this.close(id);
            } else {
                this.open(id);
            }
        },

        isOpen(id: string): boolean {
            return this.modals[id]?.isOpen ?? false;
        },

        hasOpenModals(): boolean {
            return Object.values(this.modals).some(modal => modal.isOpen);
        }
    } as ModalCommonStore);

    // Directive: x-modal-common:register="modalId"
    // Registers a modal and syncs with local Alpine state
    Alpine.directive('modal-common', (el, { value, expression }, { cleanup }) => {
        const modalId = expression;

        if (value === 'register') {
            // Register modal on init
            const store = Alpine.store('modalCommon') as ModalCommonStore;
            store.register(modalId);

            // Cleanup on destroy
            cleanup(() => {
                store.unregister(modalId);
            });
        }
    });

    // Directive: x-modal-close
    // Closes the parent modal when clicked
    // Usage: <button x-modal-close>Close</button>
    Alpine.directive('modal-close', (el, _, { cleanup }) => {

        const handler = () => {
            // Find the parent modal container (the one with the modal id)
            // The structure is: div#modal-id > div.backdrop + div.modal-content > ... > button
            const modalContainer = el.closest('[id]');
            if (modalContainer && modalContainer.id) {
                (Alpine.store('modalCommon') as ModalCommonStore).close(modalContainer.id);
            } else {
                console.warn('[x-modal-close] No modal container found');
            }
        };

        el.addEventListener('click', handler);
        cleanup(() => el.removeEventListener('click', handler));
    });

    // Global escape key handler
    window.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
            const store = Alpine.store('modalCommon') as ModalCommonStore;
            store.closeAll();
        }
    });
}

// Export store type for external use
export type { ModalCommonStore };
